import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { clearLeaf, leafFor, type RouteLeaf } from './page-title';

const LeafValue = createContext<string | null>(null);
const LeafSetter = createContext<LeafUpdate | null>(null);
const LeafRoute = createContext<string>('');

/**
 * Holds the page's own title (its last breadcrumb), which `AppLayout` adds in front of the nav entry's labels in `document.title`.
 * It is stored with the route it was set on (`pathname`, the current route), and read back only on that route.
 */
export function PageTitleLeafProvider({ pathname, children }: { pathname: string; children: ReactNode }) {
  const [leaf, setLeaf] = useState<RouteLeaf | null>(null);
  // Written in render: the provider renders before the page's layout effect runs, so a page that mounts with the route stamps the new one.
  const route = useRef(pathname);
  route.current = pathname;
  const set = useCallback<LeafUpdate>((update) => {
    setLeaf((current) => {
      const title = update(current?.title ?? null);
      return title === null ? null : { pathname: current && current.title === title ? current.pathname : route.current, title };
    });
  }, []);
  return (
    <LeafSetter.Provider value={set}>
      <LeafRoute.Provider value={pathname}>
        <LeafValue.Provider value={leafFor(pathname, leaf)}>{children}</LeafValue.Provider>
      </LeafRoute.Provider>
    </LeafSetter.Provider>
  );
}

/** What the mounted page contributed on this route, or null. */
export function usePageTitleLeafValue(): string | null {
  return useContext(LeafValue);
}

type LeafUpdate = (update: (current: string | null) => string | null) => void;

/** The effect body: sets the title and returns the cleanup, which clears the slot only while it still holds this consumer's title. */
export function leafEffect(set: LeafUpdate, title: string): () => void {
  set(() => title);
  return () => set((current) => clearLeaf(current, title));
}

// Layout effect, so the new leaf is in the same commit as the new route: `usePageTitle` never composes the old page's name with the
// new nav entry. It is a plain effect where there is no DOM (`useLayoutEffect` warns on the server).
const useLeafEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

/** A page (or its breadcrumb) says what it is called; cleared when it unmounts. Text only: pass null while the name is not known. */
export function usePageTitleLeaf(title: string | null): void {
  const set = useContext(LeafSetter);
  // The route is a dependency: a page that stays mounted across routes (a detail page whose tabs are nested routes) re-stamps its title on each one.
  const pathname = useContext(LeafRoute);
  useLeafEffect(() => {
    if (!set || !title) return;
    return leafEffect(set, title);
  }, [set, title, pathname]);
}
